import { apiClient } from './client'

// ============================================================
// Types
// ============================================================

export interface TelephonyTestResult {
  id: number
  run_id: string
  test_name: string
  test_category: string
  status: 'passed' | 'failed' | 'skipped' | 'error' | 'timeout'
  started_at: string | null
  completed_at: string | null
  duration_ms: number | null
  expected_value: string | null
  actual_value: string | null
  error_message: string | null
  details: Record<string, unknown> | null
  retry_count: number
}

export interface TelephonyTestRun {
  id: string
  trigger_type: 'scheduled' | 'manual'
  triggered_by: number | null
  status: 'running' | 'passed' | 'failed' | 'partial' | 'error'
  started_at: string | null
  completed_at: string | null
  duration_seconds: number | null
  total_checks: number
  passed_checks: number
  failed_checks: number
  summary: string | null
  metadata: Record<string, unknown> | null
  notification_sent: boolean
}

export interface TelephonyTestRunDetail extends TelephonyTestRun {
  results: TelephonyTestResult[]
}

export interface TelephonyTestSummary {
  enabled: boolean
  last_run: TelephonyTestRun | null
  last_run_results: TelephonyTestResult[] | null
  next_scheduled: string | null
  is_running: boolean
  total_runs_today: number
}

export interface TelephonyTestTriggerResponse {
  run_id: string
  status: string
  message: string
}

// ============================================================
// API Functions
// ============================================================

export const telephonyTestsApi = {
  /**
   * Get summary for dashboard widget
   */
  getSummary: async (): Promise<TelephonyTestSummary> => {
    const response = await apiClient.get('/telephony-tests/summary')
    return response.data
  },

  /**
   * List test runs with optional filters
   */
  listRuns: async (
    status?: string,
    limit = 20,
    offset = 0
  ): Promise<TelephonyTestRun[]> => {
    const response = await apiClient.get('/telephony-tests/runs', {
      params: { status, limit, offset },
    })
    return response.data
  },

  /**
   * Get a specific test run with all results
   */
  getRunDetail: async (runId: string): Promise<TelephonyTestRunDetail> => {
    const response = await apiClient.get(`/telephony-tests/runs/${runId}`)
    return response.data
  },

  /**
   * Trigger a manual test
   */
  triggerTest: async (
    categories?: string[]
  ): Promise<TelephonyTestTriggerResponse> => {
    const response = await apiClient.post('/telephony-tests/trigger', {
      categories: categories || null,
    })
    return response.data
  },
}

export default telephonyTestsApi
